import { supabase } from './supabase';
import { Store, StoreProduct, PriceHistory, Game } from '../types';

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
}

interface StoreFilters {
  searchText?: string;
}

interface ProductFilters {
  storeId?: string;
  gameId?: string;
  searchText?: string;
  minPrice?: number;
  maxPrice?: number;
  available?: boolean;
}

interface PriceFilters {
  gameId: string;
  storeId?: string;
  startDate?: string;
  endDate?: string;
}

// Função para buscar todas as lojas com paginação e filtros
export async function getStores(
  filters: StoreFilters = {},
  page: number = 1, 
  pageSize: number = 20
): Promise<PaginatedResponse<Store>> {
  try {
    // Iniciar a consulta básica
    let query = supabase.from('stores').select('*', { count: 'exact' });
    
    // Aplicar filtro de texto, se existir
    if (filters.searchText) {
      const searchTerm = filters.searchText.trim().toLowerCase();
      query = query.ilike('name', `%${searchTerm}%`);
    }
    
    // Aplicar paginação
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    query = query.order('name').range(from, to);
    
    // Executar consulta
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);
    
    return {
      data: data || [],
      pagination: {
        page,
        pageSize,
        totalItems,
        totalPages
      }
    };
  } catch (error) {
    console.error('Erro ao buscar lojas:', error);
    throw error;
  }
}

// Função para adicionar uma nova loja
export async function addStore(store: Omit<Store, 'id' | 'created_at' | 'updated_at'>): Promise<Store> {
  try {
    const { data, error } = await supabase
      .from('stores')
      .insert([store])
      .select()
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('Nenhum dado retornado após a inserção');
    
    return data;
  } catch (error) {
    console.error('Erro ao adicionar loja:', error);
    throw error;
  }
}

// Função para atualizar uma loja existente
export async function updateStore(id: string, updates: Partial<Store>): Promise<Store> {
  try {
    const { data, error } = await supabase
      .from('stores')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('Nenhum dado retornado após a atualização');
    
    return data;
  } catch (error) {
    console.error('Erro ao atualizar loja:', error);
    throw error;
  }
}

// Função para excluir uma loja
export async function deleteStore(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('stores')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Erro ao excluir loja:', error);
    throw error;
  }
}

// Função para buscar produtos de uma loja específica com paginação e filtros
export async function getStoreProducts(
  filters: ProductFilters = {},
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<StoreProduct>> {
  try {
    // Iniciar a consulta básica com join para dados da loja e do jogo
    let query = supabase
      .from('store_products')
      .select('*, store:stores(*), game:games(id, name, image_url, slug)', { count: 'exact' });
    
    // Aplicar filtros
    if (filters.storeId) {
      query = query.eq('store_id', filters.storeId);
    }
    
    if (filters.gameId) {
      query = query.eq('game_id', filters.gameId);
    }
    
    if (filters.minPrice !== undefined) {
      query = query.gte('price', filters.minPrice);
    }
    
    if (filters.maxPrice !== undefined) {
      query = query.lte('price', filters.maxPrice);
    }
    
    if (filters.available !== undefined) {
      query = query.eq('available', filters.available);
    }
    
    if (filters.searchText) {
      // Pesquisa por jogo usando o relacionamento
      query = query.textSearch('game.name', filters.searchText, {
        type: 'websearch',
        config: 'portuguese'
      });
    }
    
    // Aplicar paginação
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    query = query.order('updated_at', { ascending: false }).range(from, to);
    
    // Executar consulta
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);
    
    return {
      data: data || [],
      pagination: {
        page,
        pageSize,
        totalItems,
        totalPages
      }
    };
  } catch (error) {
    console.error('Erro ao buscar produtos:', error);
    throw error;
  }
}

// Função para adicionar um novo produto
export async function addStoreProduct(product: Omit<StoreProduct, 'id' | 'created_at' | 'updated_at' | 'last_checked' | 'store'>): Promise<StoreProduct> {
  try {
    const { data, error } = await supabase
      .from('store_products')
      .insert([product])
      .select('*, store:stores(*)')
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('Nenhum dado retornado após a inserção');
    
    // Se um preço foi definido, registrar no histórico
    if (product.price) {
      try {
        await addPriceToHistory(data.id, product.price);
      } catch (historyError) {
        console.error('Erro ao registrar histórico de preço:', historyError);
        // Não interrompe o fluxo se o histórico falhar
      }
    }
    
    return data;
  } catch (error) {
    console.error('Erro ao adicionar produto:', error);
    throw error;
  }
}

// Função para atualizar um produto existente
export async function updateStoreProduct(id: string, updates: Partial<StoreProduct>): Promise<StoreProduct> {
  try {
    const { data, error } = await supabase
      .from('store_products')
      .update(updates)
      .eq('id', id)
      .select('*, store:stores(*)')
      .single();
    
    if (error) throw error;
    if (!data) throw new Error('Nenhum dado retornado após a atualização');
    
    // Se o preço foi atualizado, registrar no histórico
    if (updates.price !== undefined) {
      try {
        await addPriceToHistory(id, updates.price);
      } catch (historyError) {
        console.error('Erro ao registrar histórico de preço:', historyError);
        // Não interrompe o fluxo se o histórico falhar
      }
    }
    
    return data;
  } catch (error) {
    console.error('Erro ao atualizar produto:', error);
    throw error;
  }
}

// Função para excluir um produto
export async function deleteStoreProduct(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('store_products')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Erro ao excluir produto:', error);
    throw error;
  }
}

// Função para adicionar um registro ao histórico de preços
export async function addPriceToHistory(storeProductId: string, price: number | null): Promise<PriceHistory | null> {
  if (price === null) return null;
  
  try {
    const { data, error } = await supabase
      .from('price_history')
      .insert([{
        store_product_id: storeProductId,
        price: price
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Erro ao adicionar preço ao histórico:', error);
    throw error;
  }
}

// Função para buscar o histórico de preços de um produto
export async function getPriceHistory(
  filters: PriceFilters
): Promise<PriceHistory[]> {
  try {
    // Primeiro, buscar todos os store_products para o jogo
    let productQuery = supabase
      .from('store_products')
      .select('id, store_id')
      .eq('game_id', filters.gameId);
    
    if (filters.storeId) {
      productQuery = productQuery.eq('store_id', filters.storeId);
    }
    
    const { data: products, error: productsError } = await productQuery;
    
    if (productsError) throw productsError;
    if (!products || products.length === 0) return [];
    
    // Buscar o histórico para todos os produtos encontrados
    const productIds = products.map(p => p.id);
    
    let historyQuery = supabase
      .from('price_history')
      .select('*, store_product:store_products(id, store:stores(*))')
      .in('store_product_id', productIds)
      .order('recorded_at', { ascending: true });
    
    if (filters.startDate) {
      historyQuery = historyQuery.gte('recorded_at', filters.startDate);
    }
    
    if (filters.endDate) {
      historyQuery = historyQuery.lte('recorded_at', filters.endDate);
    }
    
    const { data: history, error: historyError } = await historyQuery;
    
    if (historyError) throw historyError;
    
    return history || [];
  } catch (error) {
    console.error('Erro ao buscar histórico de preços:', error);
    throw error;
  }
}

// Função para comparar preços de um jogo em todas as lojas
export async function compareGamePrices(gameId: string): Promise<StoreProduct[]> {
  try {
    const { data, error } = await supabase
      .from('store_products')
      .select('*, store:stores(*)')
      .eq('game_id', gameId)
      .eq('available', true)
      .order('price', { ascending: true });
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Erro ao comparar preços do jogo:', error);
    throw error;
  }
}

// Buscar detalhes de um jogo específico
export async function getGameDetails(gameId: string): Promise<Game | null> {
  try {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .eq('id', gameId)
      .single();
    
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Erro ao buscar detalhes do jogo:', error);
    throw error;
  }
} 